import "./featured.scss";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { formatCurrency, formatCurrencyCompact } from "../../utils/currencyFormatter";
import { useMemo } from "react";

const Featured = () => {
  const { user } = useAuth();
  const { payments } = useData();

  // Monthly revenue goal
  const MONTHLY_GOAL = 40000; // ₱20,000 goal

  // Calculate payment statistics
  const paymentStats = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // Today's payments
    const todayPayments = payments.filter(payment => {
      if (!payment.paymentDate || payment.status !== 'Paid') return false;
      
      let paymentDate;
      // Handle different date formats
      if (typeof payment.paymentDate === 'object' && payment.paymentDate.seconds) {
        paymentDate = new Date(payment.paymentDate.seconds * 1000);
      } else if (payment.paymentDate instanceof Date) {
        paymentDate = payment.paymentDate;
      } else {
        paymentDate = new Date(payment.paymentDate);
      }
      
      return paymentDate.toDateString() === now.toDateString();
    });
    
    // This month's payments
    const thisMonthPayments = payments.filter(payment => {
      if (!payment.paymentDate || payment.status !== 'Paid') return false;
      
      let paymentDate;
      // Handle different date formats
      if (typeof payment.paymentDate === 'object' && payment.paymentDate.seconds) {
        paymentDate = new Date(payment.paymentDate.seconds * 1000);
      } else if (payment.paymentDate instanceof Date) {
        paymentDate = payment.paymentDate;
      } else {
        paymentDate = new Date(payment.paymentDate);
      }
      
      return paymentDate.getMonth() === currentMonth && paymentDate.getFullYear() === currentYear;
    });
    
    // Last month's payments
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const lastMonthPayments = payments.filter(payment => {
      if (!payment.paymentDate || payment.status !== 'Paid') return false;
      
      let paymentDate;
      // Handle different date formats
      if (typeof payment.paymentDate === 'object' && payment.paymentDate.seconds) {
        paymentDate = new Date(payment.paymentDate.seconds * 1000);
      } else if (payment.paymentDate instanceof Date) {
        paymentDate = payment.paymentDate;
      } else {
        paymentDate = new Date(payment.paymentDate);
      }
      
      return paymentDate.getMonth() === lastMonth && paymentDate.getFullYear() === lastMonthYear;
    });
    
    // Last week's payments (last 7 days)
    const lastWeekPayments = payments.filter(payment => {
      if (!payment.paymentDate || payment.status !== 'Paid') return false;
      
      let paymentDate;
      // Handle different date formats
      if (typeof payment.paymentDate === 'object' && payment.paymentDate.seconds) {
        paymentDate = new Date(payment.paymentDate.seconds * 1000);
      } else if (payment.paymentDate instanceof Date) {
        paymentDate = payment.paymentDate;
      } else {
        paymentDate = new Date(payment.paymentDate);
      }
      
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return paymentDate >= weekAgo && paymentDate <= now;
    });
    
    // Calculate totals
    const todayTotal = todayPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const thisMonthTotal = thisMonthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const lastMonthTotal = lastMonthPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const lastWeekTotal = lastWeekPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    
    // Calculate progress percentage towards monthly goal
    const progressPercentage = Math.min(100, (thisMonthTotal / MONTHLY_GOAL) * 100);
    
    return {
      todayTotal,
      thisMonthTotal,
      lastMonthTotal,
      lastWeekTotal,
      progressPercentage,
      todayCount: todayPayments.length,
      thisMonthCount: thisMonthPayments.length
    };
  }, [payments]);

  // Get tenant's apartment and payment info (moved to top level)
  const tenantPayments = useMemo(() => {
    if (user?.role !== 'tenant') return [];
    return payments.filter(payment => 
      (payment.tenantId === user.id || payment.tenantId === user.uid) && 
      payment.status === 'Paid'
    );
  }, [payments, user]);

  const thisMonthRent = useMemo(() => {
    if (user?.role !== 'tenant') return 0;
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return tenantPayments.filter(payment => {
      if (!payment.paymentDate) return false;
      
      let paymentDate;
      // Handle different date formats
      if (typeof payment.paymentDate === 'object' && payment.paymentDate.seconds) {
        paymentDate = new Date(payment.paymentDate.seconds * 1000);
      } else if (payment.paymentDate instanceof Date) {
        paymentDate = payment.paymentDate;
      } else {
        paymentDate = new Date(payment.paymentDate);
      }
      
      return paymentDate.getMonth() === currentMonth && 
             paymentDate.getFullYear() === currentYear &&
             payment.paymentType === 'Rent';
    }).reduce((sum, p) => sum + (p.amount || 0), 0);
  }, [tenantPayments, user]);

  const rentProgress = 85; // Placeholder - could be calculated based on due date

  // Tenant-specific content
  if (user?.role === 'tenant') {
    return (
      <div className="featured">
        <div className="top">
          <h1 className="title">Rent Payment Status</h1>
          <MoreVertIcon fontSize="small" />
        </div>
        <div className="bottom">
          <div className="featuredChart">
            <CircularProgressbar 
              value={rentProgress} 
              text={`${rentProgress}%`} 
              strokeWidth={5}
              styles={{
                path: {
                  stroke: `var(--success-color)`,
                },
                text: {
                  fill: `var(--text-primary)`,
                  fontSize: '16px',
                  fontWeight: '600',
                },
                trail: {
                  stroke: `var(--border-color)`,
                },
              }}
            />
          </div>
          <p className="title">Rent paid this month</p>
          <p className="amount">{formatCurrency(thisMonthRent)}</p>
          <p className="desc">
            {thisMonthRent > 0 ? 'Your rent payment is up to date.' : 'No rent payments recorded this month.'}
          </p>
          <div className="summary">
            <div className="item">
              <div className="itemTitle">This Month</div>
              <div className="itemResult positive">
                <KeyboardArrowUpOutlinedIcon fontSize="small"/>
                <div className="resultAmount">{formatCurrency(thisMonthRent)}</div>
              </div>
            </div>
            <div className="item">
              <div className="itemTitle">Total Payments</div>
              <div className="itemResult positive">
                <KeyboardArrowUpOutlinedIcon fontSize="small"/>
                <div className="resultAmount">{tenantPayments.length}</div>
              </div>
            </div>
            <div className="item">
              <div className="itemTitle">Payment History</div>
              <div className="itemResult positive">
                <KeyboardArrowUpOutlinedIcon fontSize="small"/>
                <div className="resultAmount">{formatCurrency(tenantPayments.reduce((sum, p) => sum + (p.amount || 0), 0))}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin content
  return (
    <div className="featured">
      <div className="top">
        <h1 className="title">Total Revenue</h1>
        <MoreVertIcon fontSize="small" />
      </div>
      <div className="bottom">
        <div className="featuredChart">
          <CircularProgressbar 
            value={Math.round(paymentStats.progressPercentage)} 
            text={`${Math.round(paymentStats.progressPercentage)}%`} 
            strokeWidth={5}
            styles={{
              path: {
                stroke: `var(--primary-color)`,
              },
              text: {
                fill: `var(--text-primary)`,
                fontSize: '16px',
                fontWeight: '600',
              },
              trail: {
                stroke: `var(--border-color)`,
              },
            }}
          />
        </div>
        <p className="title">Monthly Goal Progress</p>
        <p className="amount">{formatCurrency(paymentStats.thisMonthTotal)} / <span className="goal-amount">{formatCurrency(MONTHLY_GOAL)}</span></p>
        <p className="desc">
          {paymentStats.thisMonthCount > 0 
            ? `${paymentStats.thisMonthCount} payment${paymentStats.thisMonthCount > 1 ? 's' : ''} this month.`
            : 'No payments this month.'
          }
          {paymentStats.progressPercentage >= 100 
            ? ' 🎉 Monthly goal achieved!' 
            : ` ${Math.round(MONTHLY_GOAL - paymentStats.thisMonthTotal)} remaining to reach goal.`
          }
        </p>
        <div className="summary">
          <div className="item">
            <div className="itemTitle">This Month</div>
            <div className="itemResult positive">
              <KeyboardArrowUpOutlinedIcon fontSize="small"/>
              <div className="resultAmount">{formatCurrencyCompact(paymentStats.thisMonthTotal)}</div>
            </div>
          </div>
          <div className="item">
            <div className="itemTitle">Today</div>
            <div className="itemResult positive">
              <KeyboardArrowUpOutlinedIcon fontSize="small"/>
              <div className="resultAmount">{formatCurrencyCompact(paymentStats.todayTotal)}</div>
            </div>
          </div>
          <div className="item">
            <div className="itemTitle">Last Week</div>
            <div className="itemResult positive">
              <KeyboardArrowUpOutlinedIcon fontSize="small"/>
              <div className="resultAmount">{formatCurrencyCompact(paymentStats.lastWeekTotal)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Featured;
